import { Component, HostListener, OnDestroy } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { AlertController, LoadingController, ModalController, ToastController } from "@ionic/angular";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { BaseClass } from "src/app/shared/classes/base";
import { ProcessConfig, QuestionConfig } from "src/app/shared/interfaces/process-config.interface";
import { ProcessStatusEnum } from "src/app/shared/interfaces/process.interface";
import { UserPreview } from "src/app/shared/interfaces/user-preview.interface";
import { NewGoalsInput } from "src/app/shared/modals/new-goals-input/new-goals-input";
import { QuestionAttributePipe } from "src/app/shared/pipes/question-attribute.pipe";
import { TranslationService } from "src/app/shared/services/translation.service";
import { environment } from "src/environments/environment";
import { GoalFeedback } from "../../_interfaces/goal-feedback.interface";
import { Goal, GoalStatusEnum } from "../../_interfaces/goal.interface";
import { TwoPlusTwoCoaching, TwoPlusTwoCoachingStatus } from "../../_interfaces/two-plus-two-coaching.interface";
import { GoalsService } from "../../goals.service";

@Component({
    selector: "app-review-input-validation",
    templateUrl: "./review-input-validation.page.html",
    styleUrls: ["./review-input-validation.component.scss"],
})
export class ReviewInputValidationPage extends BaseClass implements OnDestroy {
    public hideGoalsOnGoalsReview = environment.features.hideGoalsOnGoalsReview;
    public showSkipTwoPlusTwoCoachingEvaluationFlag = environment.features.showSkipTwoPlusTwoCoachingEvaluation;
    public showSkipTwoPlusTwoCoachingEvaluation = false;

    public user: UserPreview;
    public inputConfig: ProcessConfig;
    public reviewConfig: ProcessConfig;
    public coachingConfig: ProcessConfig;
    public coachingForm: UntypedFormGroup;
    public goals: Array<Goal>;
    public displayedGoals: Array<Goal> = [];
    public userPreview: UserPreview;
    public subordinateGoals: any;
    public generalComments: string;
    public selectedLanguage: string;
    public canValidate: boolean = false;
    public isUserAttributesInsideConsentGoals: boolean = false;

    private _participantId: string;
    private _searchSubject: Subject<string> = new Subject();
    private _returnPath: string = undefined;

    private readonly _cycleId: number;
    private readonly _employeeId: string;

    constructor(
        protected _toastController: ToastController,
        protected _translationService: TranslationService,
        private _modalController: ModalController,
        private _loadingController: LoadingController,
        private _alertController: AlertController,
        private _router: Router,
        private _goalsService: GoalsService,
        private _activatedRoute: ActivatedRoute,
        private _questionAttributePipe: QuestionAttributePipe
    ) {
        super(_toastController, _translationService);
        this._employeeId = this._activatedRoute.snapshot.paramMap.get("userId");
        this._cycleId = parseInt(
            this._activatedRoute.snapshot.paramMap.get("cycleId"),
            10
        );
        this._setSearchSubscription();
    }

    ionViewWillEnter() {
        this.selectedLanguage = this._translationService.getSelectedLanguage();
        this._loadGoals(this._cycleId, this._employeeId);
        this._returnPath = localStorage.getItem("GoalsReview_ReturnPath");
    }

    public getColumnSize(questionsCount: number): number {
        let tableSize = 10;
        if (this.inputConfig) {
            tableSize = this.inputConfig.title ? tableSize - 2 : tableSize;
            tableSize = this.inputConfig.weight ? tableSize - 2 : tableSize;
            tableSize = this.inputConfig.deadline ? tableSize - 2 : tableSize;
            tableSize = this.inputConfig.description ? tableSize - 2 : tableSize;
        }

        let columnSize = questionsCount ? parseFloat((tableSize / questionsCount).toFixed(2).toString()) : null;

        if (columnSize) return columnSize;
        return questionsCount > 1 ? 1 : 2;
    }

    public updateSearch(searchTextValue: string) {
        this._searchSubject.next(searchTextValue);
    }

    public setApprovedAndClearComment(question: any) {
        if (this.canValidate) {
            question.approved = true;
            this.coachingForm.get('field' + question.order + 'Comment').setValue('');
        }
    }

    public setGoalsFeedbacks(goals: Array<Goal>): void {
        const feedbacks = this.reviewConfig.twoPlusTwoCoachingIntegration ? this._getGoalsFeedbacks(goals) : null;
        const isTwoPlusTwoCoachingIntegration = this.reviewConfig.twoPlusTwoCoachingIntegration;
        let questions = this.coachingConfig.questions;
        let coachingFormVar = this.coachingForm.getRawValue();
        questions.forEach((question) => {
            if (question.approved) {
                let field = "field" + question.order + "Comment";
                if (!this.showSkipTwoPlusTwoCoachingEvaluation) {
                    coachingFormVar[field] = field.includes("field4") ? coachingFormVar[field] : "";
                }
            }
        });

        const coaching = this._getCoachingReview(questions, coachingFormVar);
        const questionsToHideFlagged = this._questionAttributePipe.transform(this.coachingConfig.questions, this.user.attributes);
        let allRequiredAnswered = true;
        for (let i = 0; i < questionsToHideFlagged.length; i++) {
            const question = questionsToHideFlagged[i];
            const fieldName = `field${question.order}`;

            const fieldValue = coaching[fieldName as keyof typeof coaching];

            if (question.required && !question.hideQuestion) {
                if (fieldValue == null || fieldValue === '') {
                    allRequiredAnswered = false;
                    break;
                }
            }
        }
        if (coaching && !this.showSkipTwoPlusTwoCoachingEvaluationFlag && this.reviewConfig.twoPlusTwoCoachingIntegration && !allRequiredAnswered) {
            this.notify(this._translationService.getTranslation("generic.missingFields"), "danger");
        } else {
            if ((!this.reviewConfig.twoPlusTwoCoachingIntegration && coaching && !feedbacks) || (this.reviewConfig.twoPlusTwoCoachingIntegration && feedbacks && coaching)) {
                this._saveFeedbacks(coaching, feedbacks, isTwoPlusTwoCoachingIntegration, allRequiredAnswered);
            }
        }
    }
    public async confirmDeleteGoal(goal: Goal): Promise<void> {
        const alert = await this._alertController.create({
            message: this._translationService.getTranslation("goalProgressInfo.confirmDeleteGoal"),
            buttons: [
                {
                    text: this._translationService.getTranslation("generic.no"),
                    role: "cancel",
                    cssClass: "danger",
                },
                {
                    text: this._translationService.getTranslation("generic.yes"),
                    handler: () => {
                        this._deleteGoal(goal.id);
                    },
                },
            ],
        });

        await alert.present();
    }

    public async openNewGoalModal() {
        const modal = await this._modalController.create({
            component: NewGoalsInput,
            cssClass: "new-goals-input",
            componentProps: {
                cycleParticipantId: this._participantId,
                processConfig: this.inputConfig,
                processType: this.ProcessType.GoalsReviewValidation,
            },
        });

        modal.onDidDismiss().then((dismissEvent) => {
            if (dismissEvent.data) {
                this._loadGoals(this._cycleId, this._employeeId);
            }
        });

        return await modal.present();
    }

    public goBack(): void {
        this._router.navigate([this._returnPath ? this._returnPath : "goals-review-validation/" + this._cycleId]);
    }

    public setQuestionIndex(question: QuestionConfig) {
        question.currentIndex = question.order;
        return question;
    }

    public setRangeValue(key: string, value: string): void {
        if (!this.coachingForm.get(key).disabled && this.canValidate) this.coachingForm.get(key).setValue(value);
    }

    public showRequiredBadge(formGroup: UntypedFormGroup, index: number): boolean {
        return formGroup["missingFields"] && !formGroup.get("field" + index + "comment").valid;
    }

    private async _loadGoals(cycleId: number, employeeId: string): Promise<void> {
        const loading = await this._loadingController.create();
        await loading.present();

        this._goalsService.getUserGoalsForReview(cycleId, employeeId).subscribe(
            (response) => {
                this._participantId = response.data.cycleParticipantId;

                this.inputConfig = response.data.goalInputConfig;
                this.reviewConfig = response.data.goalReviewConfig;
                this.showSkipTwoPlusTwoCoachingEvaluation = this.showSkipTwoPlusTwoCoachingEvaluationFlag && this.reviewConfig.skipTwoPlusTwoCoachingEvaluation;
                this.isUserAttributesInsideConsentGoals = response.data.isUserAttributesInsideConsentGoals;

                response.data.goals.forEach((goal: Goal) => {
                    goal.approved = !(goal.status === GoalStatusEnum.Rejected || goal.status === GoalStatusEnum.ReviewRejected);
                });

                this.user = response.data.user;
                this.goals = response.data.goals;
                this.displayedGoals = response.data.goals;
                this.userPreview = this._setUserPreview(this.goals, this.user);

                this.canValidate =
                    this.goals.some(
                        (g) =>
                            response.data.processStatus !== ProcessStatusEnum.AllowedViewing &&
                            (g.status === GoalStatusEnum.WaitingForApproval ||
                                g.status === GoalStatusEnum.Rejected ||
                                g.status === GoalStatusEnum.DraftForReview ||
                                g.status === GoalStatusEnum.SentForReview ||
                                g.status === GoalStatusEnum.ReviewRejected ||
                                g.status === GoalStatusEnum.ReviewAccepted ||
                                (response.data.userTwoPlusTwoCoachingRecord &&
                                    response.data.userTwoPlusTwoCoachingRecord.status === TwoPlusTwoCoachingStatus.AwaitingAuthorization) ||
                                !response.data.goalReviewConfig.twoPlusTwoCoachingIntegration),
                    ) ||
                    this.hideGoalsOnGoalsReview ||
                    this.isUserAttributesInsideConsentGoals;

                this.coachingConfig = response.data.twoPlusTwoCoachingConfig;
                this.coachingForm = this._createCoachingFormGroup(this.coachingConfig.questions, response.data.userTwoPlusTwoCoachingRecord);
                loading.dismiss();
            },
            (error) => {
                loading.dismiss();
                this.notify(this.getErrorNotification(error), "danger");
            },
        );
    }

    private _createCoachingFormGroup(questions: Array<QuestionConfig>, coachingRecord: TwoPlusTwoCoaching): UntypedFormGroup {
        const formGroup = new UntypedFormGroup({});
        const displayedQuestions = this._questionAttributePipe.transform(questions, this.user.attributes);

        questions.forEach((question, index) => {
            const validators = displayedQuestions.some((g) => g.id === question.id) && (question.required || question.goal) ? [Validators.required] : [];
            const controlName = "field" + (index + 1);
            formGroup.addControl(
                controlName,
                new UntypedFormControl(
                    {
                        value: coachingRecord ? coachingRecord[controlName] : "",
                        disabled: this.canValidate === false,
                    },
                    validators,
                ),
            );

            const commentControl = "field" + (index + 1) + "Comment";
            formGroup.addControl(
                commentControl,
                new UntypedFormControl({
                    value: coachingRecord ? coachingRecord[commentControl] : "",
                    disabled: this.canValidate === false,
                }),
            );

            question.approved = !coachingRecord || (coachingRecord && !coachingRecord[commentControl]);
            question.currentIndex = index + 1;
        });

        this.generalComments = coachingRecord ? coachingRecord.managerComment : "";

        return formGroup;
    }

    private _setUserPreview(goals: Array<Goal>, user: UserPreview): UserPreview {
        if (this.hideGoalsOnGoalsReview) {
            return {
                imageUrl: user ? user.imageUrl : null,
                username: user ? user.username : null,
                name: user ? user.name : null,
            };
        }
        if (!goals || goals.length === 0) return null;
        return {
            imageUrl: goals[0].userImgUrl,
            username: goals[0].userName,
        };
    }

    private _setSearchSubscription() {
        this._searchSubject.pipe(distinctUntilChanged(), debounceTime(1000)).subscribe((searchValue: string) => {
            this.displayedGoals = this.goals.filter((goal) => {
                return goal.title.toLowerCase().includes(searchValue.toLowerCase().trim());
            });
        });
    }

    private _deleteGoal(goalId: number): void {
        this._goalsService.deleteGoal(goalId).subscribe(
            () => {
                this._loadGoals(this._cycleId, this._employeeId);
                this.notify(this._translationService.getTranslation("goalsInput.goalDeleteSuccess"), "success");
            },
            (error) => this.notify(this.getErrorNotification(error), "danger"),
        );
    }

    private _getGoalsFeedbacks(goals: Array<Goal>): Array<GoalFeedback> {
        if (goals.some((g) => g.approved === null || g.approved === undefined)) {
            this.notify("goalsValidation.missingApprovals", "danger");
            return null;
        }

        if (goals.some((g) => g.approved === false && !g.managerComment)) {
            this.notify(this._translationService.getTranslation("goalsValidation.missingRejectedFeedback"), "danger");
            return null;
        }

        return goals.map((g) => {
            return {
                goalId: g.id,
                approved: g.approved,
                comment: g.managerComment,
            };
        });
    }

    private _getCoachingReview(questions: Array<QuestionConfig>, coaching: TwoPlusTwoCoaching): TwoPlusTwoCoaching {
        if (questions.some((g) => g.approved === null || g.approved === undefined)) {
            this.notify("goalsValidation.missingApprovals", "danger");
            return null;
        }

        for (let index = 0; index < questions.length; index++) {
            const question = questions[index];
            if (question.approved === false && !coaching["field" + (index + 1) + "Comment"]) {
                this.notify(this._translationService.getTranslation("goalsValidation.missingRejectedFeedback"), "danger");
                return null;
            }
            if (this.showSkipTwoPlusTwoCoachingEvaluationFlag && question.required && !question.hideQuestion && !coaching["field" + (question.order)]) {
                this.notify(this._translationService.getTranslation("generic.requiredValueNotProvided"), "danger");
                this.coachingForm["missingFields"] = true;
                return null;
            }
        }

        coaching.generalComments = this.generalComments;
        return coaching;
    }

    private _saveFeedbacks(coaching: TwoPlusTwoCoaching, feedbacks: Array<GoalFeedback>, isTwoPlusTwoCoachingIntegration: boolean, allRequiredAnswered: boolean): void {
        this._goalsService.saveGoalsReviewValidation(this._participantId, coaching, feedbacks, isTwoPlusTwoCoachingIntegration, allRequiredAnswered).subscribe(
            () => {
                this.notify(this._translationService.getTranslation("generic.infoSavedSuccessfully"), "success");
                this.goBack();
            },
            (error) => this.notify(this.getErrorNotification(error), "danger"),
        );
    }

    @HostListener("window:beforeunload")
    ngOnDestroy() {
        this._searchSubject.unsubscribe();
        localStorage.removeItem("GoalsReview_ReturnPath");
    }
}
